import { zodResolver } from '@hookform/resolvers/zod';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckIcon from '@mui/icons-material/Check';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { UserContext } from '~/auth/userContext';
import DialogBackNextActions from '~/components/DialogBackNextActions';
import DialogTitleWithCloseButton from '~/components/DialogTitleWithCloseButton';
import type { FieldDef } from '~/components/form/utils';
import { createFormInvalidHandler, FieldAndCell } from '~/components/form/utils';
import { invoicesNotesCreate } from '~/data/generated/endpoints/invoices/invoices';
import { userManagementInviteCreate } from '~/data/generated/endpoints/user-management/user-management';
import { usersSendSpendingNotificationCreate } from '~/data/generated/endpoints/users/users';
import type { SpendingClassEnum, SpendingNotificationRequest } from '~/data/generated/model';
import useAccessibleClientGroups from '~/data/hooks/useAccessibleClientGroups';
import useCustomerAccounts from '~/data/hooks/useCustomerAccounts';
import type {
  AiReviewStatus,
  ApproveInvoiceForm,
  ApproveInvoiceFormValues,
  Invoice,
  PartialApproveInvoiceForm,
  PartialApproveInvoiceFormValues,
  RejectInvoiceForm,
  RejectInvoiceFormValues,
  ResetInvoiceForm,
} from '~/data/schemas/invoice';
import {
  approveInvoiceFormSchema,
  InvoiceActionType,
  InvoiceKind,
  invoiceKindToLabelMap,
  InvoiceRejectionCategoryOptions,
  partialApproveInvoiceFormSchema,
  rejectInvoiceFormSchema,
  resetInvoiceFormSchema,
} from '~/data/schemas/invoice';
import useLAISnackbar from '~/hooks/useLAISnackbar';
import { useLocaleTerms } from '~/hooks/useLocaleTerms';
import { useUserPermissionCheck } from '~/hooks/useUserPermissionCheck';
import { BUTTON_SX } from '~/theme';

interface IBaseProps {
  invoice: Invoice;
  isSaving: boolean;
  onClose: () => void;
  revalidateNotes?: () => void;
}

interface IPartialApproveDialogProps extends IBaseProps {
  onSubmit: (invoice: PartialApproveInvoiceForm) => void;
}

interface IRejectDialogProps extends IBaseProps {
  onSubmit: (invoice: RejectInvoiceForm) => void;
}

interface IApproveDialogProps extends IBaseProps {
  onSubmit: (invoice: ApproveInvoiceForm) => void;
}

interface IResetDialogProps extends IBaseProps {
  onSubmit: (invoice: { reason: string }) => void;
}

// Multi-step flow state management
type InvoiceActionStep = 'review' | 'notify';

interface InvoiceActionFlowState {
  currentStep: InvoiceActionStep;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formData: any;
  notificationOptions: {
    notifyClient: boolean;
    selectedUsers: string[];
  };
}

// Step indicator component
interface StepIndicatorProps {
  activeStep: number;
  steps: string[];
}

const StepIndicator: React.FC<StepIndicatorProps> = ({ activeStep, steps }) => {
  return (
    <Box sx={{ width: '50%', mb: 3, px: 2 }}>
      <Stepper activeStep={activeStep}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
};

// Notification step component
interface NotificationStepProps {
  invoice: Invoice;
  actionType: InvoiceActionType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formData: any;
  onBack: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSubmit: (data: any, notificationOptions: InvoiceActionFlowState['notificationOptions']) => void;
  isSaving: boolean;
}

// Helper function to send spending notifications
const sendSpendingNotifications = async (invoice: Invoice, selectedUsers: string[]) => {
  if (selectedUsers.length === 0) return { ok: true };

  try {
    await Promise.all(
      selectedUsers.map(async (email) => {
        // Build the request in the generated (camelCase) shape so the wire
        // payload matches what the schema declares; only the enum value needs a
        // cast since the label map returns a plain string (AMP-503).
        const notificationRequest: SpendingNotificationRequest = {
          spendingClass: (invoiceKindToLabelMap.get(invoice.kind) ||
            'Invoice') as SpendingClassEnum,
          spendingNumber: invoice.number,
          spendingProvider: invoice.serviceProvider?.name || '',
          spendingProperty: invoice.property.name,
          spendingId: invoice.id,
          clientEmail: email,
          targetUrl: window.location.origin,
        };

        return usersSendSpendingNotificationCreate(notificationRequest);
      }),
    );

    return {
      ok: true,
      message: `Spending notification sent to ${selectedUsers.length} client(s)`,
    };
  } catch (error) {
    console.error('Failed to send spending notification:', error);

    const errorMessage =
      error instanceof Error ? error.message : 'Failed to send spending notification';

    return { ok: false, error: errorMessage };
  }
};

const getLocalStorageEmails = (invoice: Invoice) => {
  if (!invoice.property.portfolio) return [];
  const key = `${invoice.property.portfolio.id}-emails`;
  const existingUsers = localStorage.getItem(key);
  return existingUsers ? JSON.parse(existingUsers) : [];
};

const NotificationStep: React.FC<NotificationStepProps> = ({
  invoice,
  formData,
  onBack,
  onSubmit,
  isSaving,
}) => {
  const { userDetails } = useContext(UserContext);
  const terms = useLocaleTerms();
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [users, setUsers] = useState<string[]>(getLocalStorageEmails(invoice));

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [invitesSending, setInvitesSending] = useState(false);
  const [inviteCustomerAccount, setInviteCustomerAccount] = useState<string | null>(null);
  const [inviteClientGroup, setInviteClientGroup] = useState<string | null>(null);
  const [inviteUsers, setInviteUsers] = useState<string[]>([]);
  const [inviteEmails, setInviteEmails] = useState<string[]>([]);

  // Fetch data for dropdowns
  const { customerAccounts, isLoading: isLoadingCustomerAccounts } = useCustomerAccounts();
  const { accessibleClientGroups, isLoading: isLoadingClientGroups } = useAccessibleClientGroups();

  const { showSnackbar } = useLAISnackbar();

  // Use the extracted user permission check hook
  const {
    permissionResults,
    permissionErrors,
    checkingPermissions,
    checkUserPermissions,
    hasPermission,
    setPermissionErrors,
  } = useUserPermissionCheck();

  const handleUserSelection = useCallback(
    async (newUsers: string[]) => {
      setSelectedUsers(newUsers);
      const usersOptions = new Set([...newUsers, ...getLocalStorageEmails(invoice)]);
      setUsers(Array.from(usersOptions));

      // Clear errors for removed users
      setPermissionErrors((prev) => {
        const newErrors: Record<string, string> = {};
        newUsers.forEach((email) => {
          if (prev[email]) {
            newErrors[email] = prev[email];
          }
        });
        return newErrors;
      });

      // Use the extracted permission checking logic
      await checkUserPermissions(newUsers, invoice);
    },
    [invoice, checkUserPermissions, setPermissionErrors],
  );

  const handleInviteUserSelection = useCallback(
    (newUsers: string[]) => {
      const inviteUsersOptions = new Set([...newUsers, ...inviteUsers]);
      setInviteUsers(Array.from(inviteUsersOptions));
    },
    [inviteUsers],
  );

  const handleInviteClose = useCallback(() => {
    setShowInviteModal(false);
    setInviteEmails([]);
    setInviteCustomerAccount(null);
    setInviteClientGroup(null);
  }, []);

  const handleInviteSubmit = useCallback(async () => {
    if (!inviteCustomerAccount || !inviteClientGroup || inviteEmails.length === 0) {
      return;
    }

    setInvitesSending(true);

    try {
      // The legacy bulk `/users/invite-users/` endpoint was removed; invite each
      // client through the user-management invite endpoint (the live path).
      await Promise.all(
        inviteEmails.map((email) =>
          userManagementInviteCreate({
            email,
            role: 'client',
            customerAccounts: [inviteCustomerAccount],
            clientGroup: inviteClientGroup,
          }),
        ),
      );

      showSnackbar(`Invitation${inviteEmails.length > 1 ? 's' : ''} sent`, { variant: 'success' });
      handleInviteClose();
      if (selectedUsers.length > 0) {
        await checkUserPermissions(selectedUsers, invoice);
      }
    } catch (error) {
      if (error instanceof Error) {
        showSnackbar(error.message, { variant: 'error' });
      } else {
        showSnackbar('Error inviting users', { variant: 'error' });
      }
    } finally {
      setInvitesSending(false);
    }
  }, [
    inviteEmails,
    inviteCustomerAccount,
    inviteClientGroup,
    handleInviteClose,
    showSnackbar,
    checkUserPermissions,
    selectedUsers,
    invoice,
  ]);

  const handleNext = useCallback(
    (shouldSendNotification: boolean = false) => {
      const notificationOptions = {
        notifyClient: shouldSendNotification,
        selectedUsers: shouldSendNotification ? selectedUsers : [],
      };
      onSubmit(formData, notificationOptions);
    },
    [onSubmit, formData, selectedUsers],
  );

  const canProceed = useCallback(() => {
    return (
      selectedUsers.length > 0 &&
      !checkingPermissions &&
      selectedUsers.every((email) => hasPermission(email))
    );
  }, [selectedUsers, checkingPermissions, hasPermission]);

  return (
    <>
      <StepIndicator activeStep={1} steps={['Review', 'Send Notification']} />
      <DialogContent sx={{ overflowY: 'visible' }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Autocomplete
              multiple
              freeSolo
              options={users}
              value={selectedUsers}
              onChange={(event, newValue) => {
                void handleUserSelection(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`Send Notification of Reviewed ${invoiceKindToLabelMap.get(invoice.kind)} To`}
                  placeholder="Enter a user email..."
                />
              )}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox checked={selected} />
                  <Box>
                    <Typography variant="body2">{option}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option}
                    </Typography>
                  </Box>
                </li>
              )}
              filterOptions={(options, { inputValue }) => {
                const filterValue = inputValue.toLowerCase();
                const filtered = options.filter((option) =>
                  option.toLowerCase().includes(filterValue),
                );
                // If inputValue is not empty and not already in options, add as new option
                if (inputValue && !options.some((option) => option.toLowerCase() === filterValue)) {
                  filtered.push(inputValue);
                }
                return filtered;
              }}
            />

            {checkingPermissions ? (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Checking permissions...
                </Typography>
              </Box>
            ) : null}

            {Object.keys(permissionResults).length > 0 && !checkingPermissions && (
              <Box sx={{ mt: 2 }}>
                {selectedUsers.map((email) => {
                  const result = permissionResults[email];
                  if (!result) return null;

                  return (
                    <Box key={email} sx={{ mb: 1 }}>
                      <Alert
                        severity={hasPermission(email) ? 'success' : 'error'}
                        sx={{ borderRadius: 10, p: 0, display: 'flex', alignItems: 'center' }}
                        icon={
                          result.hasPermission ? (
                            <CheckIcon color="success" sx={{ p: 0, ml: 1, mr: 1, fontSize: 20 }} />
                          ) : (
                            <ReportProblemIcon
                              color="warning"
                              sx={{ p: 0, ml: 1, mr: 1, fontSize: 20 }}
                            />
                          )
                        }
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            color: result.hasPermission ? 'success.main' : 'warning.main',
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 'bold',
                            }}
                          >
                            {email}&nbsp;
                          </Typography>
                          <Typography>
                            {hasPermission(email)
                              ? 'is a user of Lift AI.'
                              : 'is not a Lift AI user.'}
                            &nbsp;
                          </Typography>
                          {permissionErrors[email] ? (
                            <Typography
                              sx={{
                                fontWeight: 'bold',
                                color: 'warning.main',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                              }}
                              onClick={() => {
                                setInviteEmails([email]);
                                setInviteUsers(
                                  selectedUsers.filter((email) => permissionErrors[email]),
                                );
                                setShowInviteModal(true);
                              }}
                            >
                              Invite to Lift AI?
                            </Typography>
                          ) : null}
                        </Box>
                      </Alert>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Box sx={{ mb: 2 }}>
              <Typography
                color="primary"
                onClick={() => setShowPreviewModal(true)}
                sx={{
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  width: 'fit-content',
                }}
              >
                PREVIEW NOTIFICATION
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button variant="outlined" onClick={onBack} disabled={isSaving} sx={BUTTON_SX}>
          Back
        </Button>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => handleNext(false)}
            disabled={isSaving}
            sx={BUTTON_SX}
          >
            Skip
          </Button>
          <Button
            variant="contained"
            onClick={() => handleNext(true)}
            disabled={isSaving || !canProceed()}
            sx={BUTTON_SX}
          >
            Send Notification
          </Button>
        </Box>
      </Box>

      {/* Preview Notification Modal */}
      <Dialog open={showPreviewModal} onClose={() => setShowPreviewModal(false)} maxWidth="sm">
        <DialogTitleWithCloseButton
          title="Notification Preview"
          onClose={() => setShowPreviewModal(false)}
        />
        <DialogContent>
          <Box sx={{ p: 2, border: '1px solid #e0e0e0', bgcolor: '#f5f5f5' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <img src="/images/lift-logo.svg" style={{ width: '100px' }} alt="Lift AI" />
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                mb: 2,
                bgcolor: 'white',
                boxShadow: 3,
                borderTop: 0,
                borderRadius: 2,
                position: 'relative',
                overflow: 'hidden',
                p: 4,
                // Add a pseudo-element for the gradient border
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '4px',
                  background: 'linear-gradient(90deg, #1976d2 0%, #43ea7f 100%)',
                  zIndex: 1,
                },
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontSize: '20px',
                  color: '#17437c',
                  textAlign: 'center',
                }}
              >
                Your elevator consultant has completed their review of your{' '}
                {invoiceKindToLabelMap.get(invoice.kind)} #{invoice.number}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  textAlign: 'center',
                  mt: 4,
                  fontFamily: 'Roboto',
                }}
              >
                Service Provider: {invoice.serviceProvider?.name}
                <br />
                Property: {invoice.property.name}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  textAlign: 'center',
                  mt: 2,
                  fontFamily: 'Roboto',
                }}
              >
                If you have any questions about this review, please contact your consultant directly
                at {userDetails.email}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Typography
                  color="primary"
                  sx={{
                    backgroundColor: '#17437c',
                    color: 'white',
                    padding: '10px 20px',
                    borderRadius: '5px',
                  }}
                >
                  Access the review
                </Typography>
              </Box>
            </Box>
            <Typography
              variant="body2"
              sx={{ display: 'flex', justifyContent: 'center', fontSize: '10px' }}
            >
              ``
            </Typography>
            <Typography
              variant="body2"
              color="primary"
              sx={{ display: 'flex', justifyContent: 'center', fontSize: '10px' }}
            >
              Unsubscribe - Unsubscribe Preferences
            </Typography>
          </Box>
        </DialogContent>
        <DialogBackNextActions
          onBack={() => setShowPreviewModal(false)}
          actionBtns={{
            back: { label: 'Close', enabled: true },
          }}
        />
      </Dialog>

      {/* Invite Client Portal Access Modal */}
      <Dialog open={showInviteModal} onClose={handleInviteClose} maxWidth="sm" fullWidth>
        <DialogTitleWithCloseButton
          title="Client Portal Access Request Form"
          onClose={handleInviteClose}
        />
        <DialogContent sx={{ overflowY: 'visible' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography color="text.secondary">
              Please fill out this form to invite a client to Lift AI&apos;s Customer Portal
            </Typography>

            <Autocomplete
              multiple
              freeSolo
              options={inviteUsers}
              value={inviteEmails}
              onChange={(event, newValue) => {
                setInviteEmails(newValue);
                handleInviteUserSelection(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Customer Email/s"
                  placeholder="Enter a user email..."
                />
              )}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox checked={selected} />
                  <Box>
                    <Typography variant="body2">{option}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option}
                    </Typography>
                  </Box>
                </li>
              )}
              filterOptions={(options, { inputValue }) => {
                const filterValue = inputValue.toLowerCase();
                const filtered = options.filter((option) =>
                  option.toLowerCase().includes(filterValue),
                );
                // If inputValue is not empty and not already in options, add as new option
                if (inputValue && !options.some((option) => option.toLowerCase() === filterValue)) {
                  filtered.push(inputValue);
                }
                return filtered;
              }}
            />

            <FormControl fullWidth size="small">
              <InputLabel id="customer-account-label">Customer Account</InputLabel>
              <Select
                labelId="customer-account-label"
                value={inviteCustomerAccount}
                label="Customer Account"
                onChange={(e) => setInviteCustomerAccount(e.target.value)}
                disabled={isLoadingCustomerAccounts}
              >
                {customerAccounts && Array.isArray(customerAccounts) ? (
                  customerAccounts.map((account) => (
                    <MenuItem key={account.id} value={account.id}>
                      {account.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>
                    {isLoadingCustomerAccounts ? 'Loading...' : 'No customer accounts available'}
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel id="client-group-label">Client Group</InputLabel>
              <Select
                labelId="client-group-label"
                value={inviteClientGroup}
                label="Client Group"
                onChange={(e) => setInviteClientGroup(e.target.value)}
                disabled={isLoadingClientGroups}
              >
                {accessibleClientGroups && Array.isArray(accessibleClientGroups) ? (
                  accessibleClientGroups.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>
                    {isLoadingClientGroups ? 'Loading...' : 'No client groups available'}
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography sx={{ fontWeight: 'bold' }}>
                What your clients will gain access to:
              </Typography>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 2,
                  color: 'primary.main',
                  backgroundColor: 'primary.light',
                }}
              >
                <Typography sx={{ fontWeight: 'bold', mb: 1 }}>
                  <AutoAwesomeIcon sx={{ fontSize: 20, mr: 1 }} /> Comprehensive Spending Review
                  Portal
                </Typography>
                <Box component="ul" sx={{ pl: 3, mt: 0.5 }}>
                  <li>View all reviewed invoices with consultant recommendations</li>
                  <li>Track savings metrics and review history</li>
                  <li>Export reports for accounting and budget planning</li>
                </Box>
              </Box>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 2,
                  color: 'primary.main',
                  backgroundColor: 'primary.light',
                }}
              >
                <Typography
                  sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center' }}
                >
                  <AutoAwesomeIcon sx={{ fontSize: 20, mr: 1 }} /> Performance Monitoring Dashboard
                </Typography>
                <Box component="ul" sx={{ pl: 3, mt: 0.5 }}>
                  <li>Service provider KPI tracking</li>
                  <li>Maintenance completion rates</li>
                  <li>{terms.callback} Rates</li>
                </Box>
              </Box>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 2,
                  color: 'primary.main',
                  backgroundColor: 'primary.light',
                }}
              >
                <Typography sx={{ fontWeight: 'bold', mb: 1 }}>
                  <AutoAwesomeIcon sx={{ fontSize: 20, mr: 1 }} /> Portfolio Monitoring Dashboard
                </Typography>
                <Box component="ul" sx={{ pl: 3, mt: 0.5 }}>
                  <li>
                    Access to all properties and {terms.units.toLowerCase()} in their portfolio
                  </li>
                  <li>
                    Centralized Information for all Maintenance Records, Contracts, and Compliance
                  </li>
                </Box>
              </Box>
            </Box>

            <Typography color="text.secondary">
              Client portal access is included in your Lift AI subscription at no additional cost.
              Clients will receive a welcome email with login instructions after submission of this
              form.
            </Typography>
          </Box>
        </DialogContent>
        <DialogBackNextActions
          onBack={handleInviteClose}
          onNext={handleInviteSubmit}
          actionBtns={{
            back: { label: 'Cancel', enabled: true },
            next: {
              label: 'SUBMIT REQUEST',
              enabled:
                inviteEmails.length > 0 &&
                inviteCustomerAccount !== null &&
                inviteClientGroup !== null &&
                !invitesSending,
              loading: invitesSending,
            },
          }}
        />
      </Dialog>
    </>
  );
};

const buildFormFields = (actionType: InvoiceActionType, invoice: Invoice) => {
  if (actionType === InvoiceActionType.RESET) {
    return {
      formFields: [
        {
          name: 'reason',
          label: 'Reason',
          type: 'text-multiline',
          gridProps: {
            xs: 12,
          },
        } as FieldDef,
      ],
    };
  }

  const formFields: FieldDef[] = [
    {
      name: 'proposedAmount',
      label: invoice.kind === InvoiceKind.Proposal ? 'Proposal Amount' : 'Invoice Amount',
      type: 'currency',
      fieldProps: {
        disabled: true,
        defaultValue: invoice.proposedAmount,
      },
      gridProps: {
        xs: 4,
      },
    },
    ...(actionType === InvoiceActionType.PARTIAL
      ? [
          {
            name: 'reviewedAmount',
            label: 'Amount After Review',
            type: 'currency',
            gridProps: {
              xs: 4,
            },
          } as FieldDef,
        ]
      : []),
    {
      name: 'savings',
      label: 'Savings',
      type: 'currency',
      fieldProps: {
        disabled: false,
        defaultValue: invoice.savings,
      },
      gridProps: {
        xs: 4,
      },
    },
    ...(actionType === InvoiceActionType.PARTIAL || actionType === InvoiceActionType.REJECT
      ? [
          {
            name: 'rejectionCategory',
            label: 'Rejection Category',
            type: 'multiple-select',
            gridProps: {
              xs: 8,
            },
            options: InvoiceRejectionCategoryOptions.map(([key, value]) => ({
              id: key,
              label: value,
            })),
          } as FieldDef,
        ]
      : []),
    {
      name: 'reason',
      label: 'Reason',
      type: 'text-multiline',
      gridProps: {
        xs: 12,
      },
    },
    {
      name: 'addStampedReason',
      label: `Add reason to stamped ${invoiceKindToLabelMap.get(invoice.kind)}`,
      type: 'checkbox',
      gridProps: {
        xs: 12,
      },
      fieldProps: {
        sx: {
          pt: 0,
        },
      },
    },
  ];

  return {
    formFields,
  };
};

const ACTION_TO_AI_STATUS: Record<string, AiReviewStatus> = {
  [InvoiceActionType.APPROVE]: 'approved',
  [InvoiceActionType.REJECT]: 'rejected',
  [InvoiceActionType.PARTIAL]: 'partial-approval',
};

/** Returns true when the AI review recommendation matches the user's chosen action. */
const insightsMatchAction = (invoice: Invoice, action: InvoiceActionType): boolean =>
  invoice.insights?.status != null && invoice.insights.status === ACTION_TO_AI_STATUS[action];

export const PartialApproveDialogContent: React.FC<IPartialApproveDialogProps> = ({
  invoice,
  onClose,
  onSubmit,
  isSaving,
  revalidateNotes,
}) => {
  const { showSnackbar } = useLAISnackbar();
  const [flowState, setFlowState] = useState<InvoiceActionFlowState>({
    currentStep: 'review',
    formData: {},
    notificationOptions: {
      notifyClient: true,
      selectedUsers: [],
    },
  });

  const titleWithInvoice = `Partially Approve ${invoiceKindToLabelMap.get(invoice.kind)} #${
    invoice.number
  } - ${invoice.property.name}`;

  const { formFields } = buildFormFields(InvoiceActionType.PARTIAL, invoice);

  const prefill = insightsMatchAction(invoice, InvoiceActionType.PARTIAL);

  const { control, handleSubmit, setValue } = useForm<
    PartialApproveInvoiceFormValues,
    unknown,
    PartialApproveInvoiceForm
  >({
    mode: 'all',
    defaultValues: {
      reviewedAmount:
        prefill && invoice.insights?.reviewedAmount != null
          ? parseFloat(invoice.insights.reviewedAmount.toFixed(2))
          : parseFloat((invoice.reviewedAmount ?? 0).toFixed(2)),
      // Preserve a reason the user already entered (AMP-489); only fall
      // back to the AI insight when the user has not written one.
      reason: invoice.reason || (prefill ? (invoice.insights?.primaryInsight ?? '') : ''),
      proposedAmount: parseFloat((invoice.proposedAmount ?? 0).toFixed(2)),
      addStampedReason: false,
      savings: invoice.savings ?? 0,
      rejectionCategory:
        prefill && invoice.insights?.rejectionCategories?.length
          ? invoice.insights.rejectionCategories
          : (invoice.rejectionCategory ?? []),
    },
    resolver: zodResolver(partialApproveInvoiceFormSchema),
  });

  const [proposedAmount, reviewedAmount] = useWatch({
    control,
    name: ['proposedAmount', 'reviewedAmount'],
  });

  useEffect(() => {
    if (proposedAmount && reviewedAmount) {
      setValue('savings', parseFloat((proposedAmount - reviewedAmount).toFixed(2)), {
        shouldValidate: true,
      });
    }
  }, [proposedAmount, reviewedAmount, setValue]);

  const handleReviewNext = useCallback((data: PartialApproveInvoiceForm) => {
    setFlowState((prev) => ({
      ...prev,
      currentStep: 'notify',
      formData: data,
    }));
  }, []);

  const handleLocalStorageUsers = useCallback(
    (users: string[]) => {
      if (!invoice.property.portfolio) return;
      const key = `${invoice.property.portfolio.id}-emails`;
      const existingUsers = localStorage.getItem(key);
      if (existingUsers) {
        const existingUsersArray = JSON.parse(existingUsers);
        const mergedUsers = Array.from(new Set([...existingUsersArray, ...users]));
        localStorage.setItem(key, JSON.stringify(mergedUsers));
      } else {
        localStorage.setItem(key, JSON.stringify(users));
      }
    },
    [invoice],
  );

  const handleNotificationBack = useCallback(() => {
    setFlowState((prev) => ({ ...prev, currentStep: 'review' }));
  }, []);

  const handleFinalSubmit = useCallback(
    async (
      data: PartialApproveInvoiceForm,
      notificationOptions: InvoiceActionFlowState['notificationOptions'],
    ) => {
      // First submit the invoice action
      onSubmit(data);

      // Then send notifications if requested and users are selected
      if (notificationOptions.notifyClient) {
        const result = await sendSpendingNotifications(invoice, notificationOptions.selectedUsers);

        if (result.ok && result.message) {
          showSnackbar(result.message, { variant: 'success' });
          handleLocalStorageUsers(notificationOptions.selectedUsers);
          // LiftAI-authored note. `invoice` is required by NoteCreateUpdateSerializer
          // (the nested route does NOT inject it). `author` only needs to be a valid
          // UUID to pass the required field: the serializer overrides it to the Lift AI
          // system user for is_lift_ai notes, so the value is discarded — reuse
          // invoice.id. Cast to the generated body at this boundary (AMP-503).
          await invoicesNotesCreate(invoice.id, {
            invoice: invoice.id,
            author: invoice.id,
            content: `Sent Notification of Completed Review to ${notificationOptions.selectedUsers.join(', ')}`,
            isLiftAi: true,
          });
          // Revalidate notes to refresh the notes list
          if (revalidateNotes) {
            revalidateNotes();
          }
        } else if (!result.ok) {
          showSnackbar(`Email notification failed: ${result.error}`, {
            variant: 'error',
            persist: true,
          });
        }
      }
    },
    [onSubmit, invoice, showSnackbar, revalidateNotes, handleLocalStorageUsers],
  );

  if (flowState.currentStep === 'notify') {
    return (
      <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitleWithCloseButton title={titleWithInvoice} onClose={onClose} />
        <NotificationStep
          invoice={invoice}
          actionType={InvoiceActionType.PARTIAL}
          formData={flowState.formData}
          onBack={handleNotificationBack}
          onSubmit={handleFinalSubmit}
          isSaving={isSaving}
        />
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm">
      <form
        noValidate
        onSubmit={handleSubmit(
          (data: PartialApproveInvoiceForm, e) => {
            e?.preventDefault();
            handleReviewNext(data);
          },
          createFormInvalidHandler<PartialApproveInvoiceFormValues>({
            formName: 'InvoiceActionsPartialApprove',
            showSnackbar,
          }),
        )}
      >
        <DialogTitleWithCloseButton title={titleWithInvoice} onClose={onClose} />
        <StepIndicator activeStep={0} steps={['Review', 'Notify Client']} />
        <DialogContent sx={{ overflowY: 'visible' }}>
          <Grid container spacing={2}>
            {formFields.map((field) => (
              <FieldAndCell key={field.name} field={field} control={control} />
            ))}
          </Grid>
        </DialogContent>
        <DialogBackNextActions
          onBack={onClose}
          actionBtns={{
            back: { label: 'Cancel', enabled: !isSaving },
            next: { label: 'Next', enabled: !isSaving },
          }}
        />
      </form>
    </Dialog>
  );
};

export const ApproveDialogContent: React.FC<IApproveDialogProps> = ({
  invoice,
  onClose,
  onSubmit,
  isSaving,
  revalidateNotes,
}) => {
  const { showSnackbar } = useLAISnackbar();
  const [flowState, setFlowState] = useState<InvoiceActionFlowState>({
    currentStep: 'review',
    formData: {},
    notificationOptions: {
      notifyClient: true,
      selectedUsers: [],
    },
  });

  const titleWithInvoice = `Approve ${invoiceKindToLabelMap.get(invoice.kind)} #${
    invoice.number
  } - ${invoice.property.name}`;

  const { formFields } = buildFormFields(InvoiceActionType.APPROVE, invoice);

  const prefill = insightsMatchAction(invoice, InvoiceActionType.APPROVE);

  const { control, handleSubmit } = useForm<ApproveInvoiceFormValues, unknown, ApproveInvoiceForm>({
    mode: 'all',
    defaultValues: {
      // Preserve a reason the user already entered (AMP-489); only fall
      // back to the AI insight when the user has not written one.
      reason: invoice.reason || (prefill ? (invoice.insights?.primaryInsight ?? '') : ''),
      addStampedReason: false,
      proposedAmount: invoice.proposedAmount,
      savings: invoice.savings ?? 0,
    },
    resolver: zodResolver(approveInvoiceFormSchema),
  });

  const handleReviewNext = useCallback((data: ApproveInvoiceForm) => {
    setFlowState((prev) => ({
      ...prev,
      currentStep: 'notify',
      formData: data,
    }));
  }, []);

  const handleLocalStorageUsers = useCallback(
    (users: string[]) => {
      if (!invoice.property.portfolio) return;
      const key = `${invoice.property.portfolio.id}-emails`;
      const existingUsers = localStorage.getItem(key);
      if (existingUsers) {
        const existingUsersArray = JSON.parse(existingUsers);
        const mergedUsers = Array.from(new Set([...existingUsersArray, ...users]));
        localStorage.setItem(key, JSON.stringify(mergedUsers));
      } else {
        localStorage.setItem(key, JSON.stringify(users));
      }
    },
    [invoice],
  );

  const handleNotificationBack = useCallback(() => {
    setFlowState((prev) => ({ ...prev, currentStep: 'review' }));
  }, []);

  const handleFinalSubmit = useCallback(
    async (
      data: ApproveInvoiceForm,
      notificationOptions: InvoiceActionFlowState['notificationOptions'],
    ) => {
      // First submit the invoice action
      onSubmit(data);

      // Then send notifications if requested and users are selected
      if (notificationOptions.notifyClient) {
        const result = await sendSpendingNotifications(invoice, notificationOptions.selectedUsers);

        if (result.ok && result.message) {
          showSnackbar(result.message, { variant: 'success' });
          handleLocalStorageUsers(notificationOptions.selectedUsers);
          // LiftAI-authored note. `invoice` is required by NoteCreateUpdateSerializer
          // (the nested route does NOT inject it). `author` only needs to be a valid
          // UUID to pass the required field: the serializer overrides it to the Lift AI
          // system user for is_lift_ai notes, so the value is discarded — reuse
          // invoice.id. Cast to the generated body at this boundary (AMP-503).
          await invoicesNotesCreate(invoice.id, {
            invoice: invoice.id,
            author: invoice.id,
            content: `Sent Notification of Completed Review to ${notificationOptions.selectedUsers.join(', ')}`,
            isLiftAi: true,
          });
          // Revalidate notes to refresh the notes list
          if (revalidateNotes) {
            revalidateNotes();
          }
        } else if (!result.ok) {
          showSnackbar(`Email notification failed: ${result.error}`, {
            variant: 'error',
            persist: true,
          });
        }
      }
    },
    [onSubmit, invoice, showSnackbar, revalidateNotes, handleLocalStorageUsers],
  );

  if (flowState.currentStep === 'notify') {
    return (
      <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitleWithCloseButton title={titleWithInvoice} onClose={onClose} />
        <NotificationStep
          invoice={invoice}
          actionType={InvoiceActionType.APPROVE}
          formData={flowState.formData}
          onBack={handleNotificationBack}
          onSubmit={handleFinalSubmit}
          isSaving={isSaving}
        />
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm">
      <form
        noValidate
        onSubmit={handleSubmit(
          (data: ApproveInvoiceForm, e) => {
            e?.preventDefault();
            handleReviewNext(data);
          },
          createFormInvalidHandler<ApproveInvoiceFormValues>({
            formName: 'InvoiceActionsApprove',
            showSnackbar,
          }),
        )}
      >
        <DialogTitleWithCloseButton title={titleWithInvoice} onClose={onClose} />
        <StepIndicator activeStep={0} steps={['Review', 'Notify Client']} />
        <DialogContent sx={{ overflowY: 'visible' }}>
          <Grid container spacing={2}>
            {formFields.map((field) => (
              <FieldAndCell key={field.name} field={field} control={control} />
            ))}
          </Grid>
        </DialogContent>
        <DialogBackNextActions
          onBack={onClose}
          actionBtns={{
            back: { label: 'Cancel', enabled: !isSaving },
            next: { label: 'Next', enabled: !isSaving },
          }}
        />
      </form>
    </Dialog>
  );
};

export const RejectDialogContent: React.FC<IRejectDialogProps> = ({
  invoice,
  onClose,
  onSubmit,
  isSaving,
  revalidateNotes,
}) => {
  const { showSnackbar } = useLAISnackbar();
  const [flowState, setFlowState] = useState<InvoiceActionFlowState>({
    currentStep: 'review',
    formData: {},
    notificationOptions: {
      notifyClient: true,
      selectedUsers: [],
    },
  });

  const titleWithInvoice = `Reject ${invoiceKindToLabelMap.get(invoice.kind)} #${
    invoice.number
  } - ${invoice.property.name}`;

  const { formFields } = buildFormFields(InvoiceActionType.REJECT, invoice);

  const prefill = insightsMatchAction(invoice, InvoiceActionType.REJECT);

  const { control, handleSubmit } = useForm<RejectInvoiceFormValues, unknown, RejectInvoiceForm>({
    mode: 'all',
    defaultValues: {
      // Preserve a reason the user already entered (AMP-489); only fall
      // back to the AI insight when the user has not written one.
      reason: invoice.reason || (prefill ? (invoice.insights?.primaryInsight ?? '') : ''),
      proposedAmount: invoice.proposedAmount ?? 0,
      addStampedReason: false,
      savings: invoice.proposedAmount ?? 0,
      rejectionCategory:
        prefill && invoice.insights?.rejectionCategories?.length
          ? invoice.insights.rejectionCategories
          : (invoice.rejectionCategory ?? []),
    },
    resolver: zodResolver(rejectInvoiceFormSchema),
  });

  const handleReviewNext = useCallback((data: RejectInvoiceForm) => {
    setFlowState((prev) => ({
      ...prev,
      currentStep: 'notify',
      formData: data,
    }));
  }, []);

  const handleLocalStorageUsers = useCallback(
    (users: string[]) => {
      if (!invoice.property.portfolio) return;
      const key = `${invoice.property.portfolio.id}-emails`;
      const existingUsers = localStorage.getItem(key);
      if (existingUsers) {
        const existingUsersArray = JSON.parse(existingUsers);
        const mergedUsers = Array.from(new Set([...existingUsersArray, ...users]));
        localStorage.setItem(key, JSON.stringify(mergedUsers));
      } else {
        localStorage.setItem(key, JSON.stringify(users));
      }
    },
    [invoice],
  );

  const handleNotificationBack = useCallback(() => {
    setFlowState((prev) => ({ ...prev, currentStep: 'review' }));
  }, []);

  const handleFinalSubmit = useCallback(
    async (
      data: RejectInvoiceForm,
      notificationOptions: InvoiceActionFlowState['notificationOptions'],
    ) => {
      // First submit the invoice action
      onSubmit(data);

      // Then send notifications if requested and users are selected
      if (notificationOptions.notifyClient) {
        const result = await sendSpendingNotifications(invoice, notificationOptions.selectedUsers);

        if (result.ok && result.message) {
          showSnackbar(result.message, { variant: 'success' });
          handleLocalStorageUsers(notificationOptions.selectedUsers);
          // LiftAI-authored note. `invoice` is required by NoteCreateUpdateSerializer
          // (the nested route does NOT inject it). `author` only needs to be a valid
          // UUID to pass the required field: the serializer overrides it to the Lift AI
          // system user for is_lift_ai notes, so the value is discarded — reuse
          // invoice.id. Cast to the generated body at this boundary (AMP-503).
          await invoicesNotesCreate(invoice.id, {
            invoice: invoice.id,
            author: invoice.id,
            content: `Sent Notification of Completed Review to ${notificationOptions.selectedUsers.join(', ')}`,
            isLiftAi: true,
          });
          // Revalidate notes to refresh the notes list
          if (revalidateNotes) {
            revalidateNotes();
          }
        } else if (!result.ok) {
          showSnackbar(`Email notification failed: ${result.error}`, {
            variant: 'error',
            persist: true,
          });
        }
      }
    },
    [onSubmit, invoice, showSnackbar, revalidateNotes, handleLocalStorageUsers],
  );

  if (flowState.currentStep === 'notify') {
    return (
      <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitleWithCloseButton title={titleWithInvoice} onClose={onClose} />
        <NotificationStep
          invoice={invoice}
          actionType={InvoiceActionType.REJECT}
          formData={flowState.formData}
          onBack={handleNotificationBack}
          onSubmit={handleFinalSubmit}
          isSaving={isSaving}
        />
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm">
      <form
        noValidate
        onSubmit={handleSubmit(
          (data: RejectInvoiceForm, e) => {
            e?.preventDefault();
            handleReviewNext(data);
          },
          createFormInvalidHandler<RejectInvoiceFormValues>({
            formName: 'InvoiceActionsReject',
            showSnackbar,
          }),
        )}
      >
        <DialogTitleWithCloseButton title={titleWithInvoice} onClose={onClose} />
        <StepIndicator activeStep={0} steps={['Review', 'Notify Client']} />
        <DialogContent sx={{ overflowY: 'visible' }}>
          <Grid container spacing={2}>
            {formFields.map((field) => (
              <FieldAndCell key={field.name} field={field} control={control} />
            ))}
          </Grid>
        </DialogContent>
        <DialogBackNextActions
          onBack={onClose}
          actionBtns={{
            back: { label: 'Cancel', enabled: !isSaving },
            next: { label: 'Next', enabled: !isSaving },
          }}
        />
      </form>
    </Dialog>
  );
};

export const ResetDialogContent: React.FC<IResetDialogProps> = ({
  invoice,
  onClose,
  onSubmit,
  isSaving,
  revalidateNotes,
}) => {
  // Reset action typically doesn't need client notification as it's an internal operation
  // So we keep this as a single-step dialog
  const { showSnackbar } = useLAISnackbar();
  const titleWithInvoice = `Undo Stamp on ${invoiceKindToLabelMap.get(invoice.kind)} #${
    invoice.number
  } - ${invoice.property.name}`;

  const { formFields } = buildFormFields(InvoiceActionType.RESET, invoice);

  const { control, handleSubmit } = useForm<ResetInvoiceForm>({
    mode: 'all',
    defaultValues: {
      reason: '',
    },
    resolver: zodResolver(resetInvoiceFormSchema),
  });

  return (
    <Dialog open onClose={onClose} maxWidth="sm">
      <form
        noValidate
        onSubmit={handleSubmit(
          (data: ResetInvoiceForm, e) => {
            e?.preventDefault();
            onSubmit(data);
          },
          createFormInvalidHandler<ResetInvoiceForm>({
            formName: 'InvoiceActionsReset',
            showSnackbar,
          }),
        )}
      >
        <DialogTitleWithCloseButton title={titleWithInvoice} onClose={onClose} />
        <DialogContent sx={{ overflowY: 'visible' }}>
          <Grid container spacing={2}>
            {formFields.map((field) => (
              <FieldAndCell key={field.name} field={field} control={control} />
            ))}
          </Grid>
        </DialogContent>
        <DialogBackNextActions
          onBack={onClose}
          actionBtns={{
            back: { label: 'Cancel', enabled: !isSaving },
            next: { label: 'Save', enabled: !isSaving },
          }}
        />
      </form>
    </Dialog>
  );
};
